import { useCallback, useEffect, useRef, useState } from 'react';
import Form, { GroupItem } from 'devextreme-react/form';
import 'devextreme-react/autocomplete';
import 'devextreme-react/date-range-box';

import { t } from 'i18next';
import { IProductDefinition } from '@/core/interfaces/usestate-interfaces';
import { useNavigate, useParams } from 'react-router-dom';
import { productGroupService } from '@/projects/platform/services/product-group-service';
import { productService } from '@/projects/platform/services/product-service';
import FormWrapper from '@/core/wrappers/form-wrapper';

import { Button } from 'devextreme-react';
import { BUTTON_CONSTANTS } from '@/core/constants/common/button-contants';
import { productBrandService } from '@/projects/platform/services/product-brand-service';
import { productMeasureUnitService } from '@/projects/platform/services/product-measure-unit-service';
import { mainProductService } from '@/projects/platform/services/main-product-service';
import clsx from 'clsx';
import DataGridWrapper from '@/core/wrappers/data-grid-wrapper';
import { RemoveMessage } from '@/core/helper/validation-rule-helper';
import { alert } from 'devextreme/ui/dialog';
import { stringFormat } from '@/core/utils/utils';

function ProductDefinition(props: any) {
    const params = useParams();
    const formRef: any = useRef<Form>();
    const submitFormRef: any = useRef<HTMLFormElement>();
    const [tab, setTab] = useState('Main')
    const [productGroups, setProductGroups] = useState([]);
    const [mainProducts, setMainProducts] = useState([]);
    const [productBrands, setProductBrands] = useState([]);
    const [productMeasureUnits, setProductMeasureUnits] = useState([]);
    const [productBarcodes, setProductBarcodes] = useState([]);
    const [isTemp, setIsTemp] = useState(false);
    const [product, setProduct] = useState<IProductDefinition>(
        {
            id: '',
            name: '',
            productCode: '',
            barcodes: [],
            productGroupId: null,
            productBrandId: null,
            productMeasureUnitId: null,
            mainProductId: null,
            isActive: true,
            isStockStored: null,
            shelfLifeDays: null,
            approvalStatus: '',
            comment: ''
        }
    );

    const navigate = useNavigate();


    const handleSubmit = useCallback(async (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                const userForm = formRef.current.props.formData;
                if (userForm.barcodes.length > 0) {
                    if (params.id) {
                        await productService.updateProduct(null, userForm);
                        navigate(`/product-list`, {});
                    } else {
                        await productService.createProduct(userForm);
                        navigate(`/product-list`, {});
                    }
                    resolve();
                } else {
                    alert(t("please_enter_at_least_one_barcode"), t("warning"));
                    reject();
                    setTab("Barcodes")
                }
            } catch (error) {
                reject(error)
            }

        })
    }, []);

    useEffect(() => {
        (async function () {
            if (params.id && product.id == '') {
                const getProduct = await productService.getProduct(params.id);
                const data = getProduct.data;
                setIsTemp(data.isTemp)
                setProductBarcodes(data.barcodes)
                setProduct({
                    id: data.id,
                    name: data.name,
                    productCode: data.productCode,
                    barcodes: data.barcodes,
                    productGroupId: data.productGroupId,
                    productBrandId: data.productBrandId,
                    productMeasureUnitId: data.productMeasureUnitId,
                    mainProductId: data.mainProductId,
                    approvalStatus: data.approvalStatus,
                    isActive: data.isActive,
                    isStockStored: data.isStockStored,
                    shelfLifeDays: data.shelfLifeDays,
                    comment: data.comment
                });
            }
        })();


        const fetchProductGroups = async () => {
            const productGroups = await productGroupService.lookupProductGroup({});
            setProductGroups(productGroups.data);
        }

        const fetchProductBrands = async () => {
            const productBrands = await productBrandService.lookupProductBrand({});
            setProductBrands(productBrands.data);
        }

        const fetchProductMeasureUnits = async () => {
            const productMeasureUnits = await productMeasureUnitService.lookupProductMeasureUnit({});
            setProductMeasureUnits(productMeasureUnits.data);
        }

        const fetchMainProducts = async () => {
            const mainProducts = await mainProductService.lookupMainProduct({});
            setMainProducts(mainProducts.data);
        }

        fetchProductGroups();
        fetchProductBrands();
        fetchProductMeasureUnits();
        fetchMainProducts();

    }, [product])

    const productGroupsEditorOptions = {
        dataSource: productGroups,
        displayExpr: 'path',
        valueExpr: 'id',
    };

    const productBrandsEditorOptions = {
        dataSource: productBrands,
        displayExpr: 'name',
        valueExpr: 'id',
    };

    const productMeasureUnitEditorOptions = {
        dataSource: productMeasureUnits,
        displayExpr: 'name',
        valueExpr: 'id',
    };

    const mainProductEditorOptions = {
        dataSource: mainProducts,
        displayExpr: 'name',
        valueExpr: 'id'
    };

    const isStockStoredEditorOptions = {
        dataSource: [
            {
                key: true,
                value: t('yes')
            },
            {
                key: false,
                value: t('no')
            }
        ],
        displayExpr: 'value',
        valueExpr: 'key',
        showClearButton: true,
        placeholder:t('unknown')
    };

    const statusEditorOptions = {
        dataSource: [false, true]
    };

    const navigateBack = async () => {
        navigate('/product-list', {})
    }
    return (
        <>
            <div className='card-body col-xl-12'>
                <div className='d-flex justify-content-end'>
                    <div className='col-auto me-5'>
                        <Button
                            text={t('back')}
                            type='normal'
                            icon='back'
                            useSubmitBehavior={false}
                            onClick={navigateBack}
                        />
                    </div>
                    <div className='col-auto'>
                        <Button
                            text={t("save")}
                            type='default'
                            width={120}
                            onClick={async () => {
                                if (formRef.current) {
                                    setTab("Main");
                                    setTimeout(() => {
                                        const validationResult = formRef.current.instance.validate();
                                        if (validationResult.isValid) {
                                            submitFormRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                                        }
                                    }, 100);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className='card card-custom m-5'>
                <div className='card-header card-header-stretch overflow-auto'>
                    <ul
                        className='nav nav-stretch nav-line-tabs fw-semibold border-transparent flex-nowrap'
                        role='tablist'
                    >
                        <li className='nav-item'>
                            <a
                                className={clsx(`nav-link cursor-pointer`, { active: tab === 'Main' })}
                                onClick={() => setTab('Main')}
                                role='tab'
                            >
                                {t("definition")}
                            </a>
                        </li>
                        <li className='nav-item'>
                            <a
                                className={clsx(`nav-link cursor-pointer`, { active: tab === 'Barcodes' })}
                                onClick={() => setTab('Barcodes')}
                                role='tab'
                            >
                                {t("barcodes")}
                            </a>
                        </li>
                    </ul>
                </div>
                <div className='card-body'>
                    <div className='tab-content pt-3'>
                        <div className={clsx('tab-pane', { active: tab === 'Main' })}>
                            <FormWrapper
                                onSubmit={handleSubmit}
                                formRef={formRef}
                                submitFormRef={submitFormRef}
                                formData={product}
                                isAddBackBtn={false}
                                simpleItemGroups={[
                                    {
                                        useColCountByScreen: true,
                                        items: [
                                            {
                                                dataField: "name",
                                                label: 'product_name',
                                                isRequired: true,
                                                requiredMessage: 'product_name'
                                            },
                                            {
                                                dataField: "productCode",
                                                label: 'product_code',
                                                isRequired: true,
                                                requiredMessage: 'product_code'
                                            },
                                            {
                                                dataField: "productGroupId",
                                                editorType: "dxSelectBox",
                                                isRequired: true,
                                                editorOptions: productGroupsEditorOptions,
                                                label: 'product_group'
                                            },
                                            {
                                                dataField: "productMeasureUnitId",
                                                editorType: "dxSelectBox",
                                                isRequired: true,
                                                editorOptions: productMeasureUnitEditorOptions,
                                                label: 'measure_unit'
                                            },
                                            {
                                                dataField: "productBrandId",
                                                editorType: "dxSelectBox",
                                                editorOptions: productBrandsEditorOptions,
                                                label: 'brand'
                                            },
                                            {
                                                dataField: "mainProductId",
                                                editorType: "dxSelectBox",
                                                editorOptions: mainProductEditorOptions,
                                                label: 'main_product'
                                            },
                                            {
                                                dataField: "isStockStored",
                                                editorType: "dxSelectBox",
                                                editorOptions: isStockStoredEditorOptions,
                                                isRequired: false,
                                                label: 'is_stock_stored',
                                            },
                                            {
                                                dataField: "shelfLifeDays",
                                                label: 'shelf_life_days',
                                                editorType: "dxNumberBox",
                                                isRequired: false
                                            },
                                            {
                                                dataField: "isActive",
                                                editorType: "dxSwitch",
                                                editorOptions: statusEditorOptions,
                                                label: 'status'
                                            },
                                            {
                                                dataField: "approvalStatus",
                                                label: 'approval_status',
                                                visible: isTemp,
                                                editorOptions: {
                                                    disabled: true
                                                }
                                            },
                                            {
                                                dataField: "comment",
                                                editorType: "dxTextArea",
                                                label: 'comment',
                                                visible: isTemp,
                                                editorOptions: {
                                                    disabled: true
                                                }
                                            }
                                        ]
                                    }
                                ]}
                            />
                            <div className='row justify-content-end'>
                                <div className='col-auto'>
                                    <Button
                                        text={t("next")}
                                        type='normal'
                                        width={120}
                                        onClick={() => {
                                            setTab("Barcodes");
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={clsx('tab-pane', { active: tab === 'Barcodes' })}>
                            <DataGridWrapper
                                props={{ wordWrapEnabled: false }}
                                height={400}
                                datasource={product.barcodes}
                                remoteOperations={false}
                                filterVisible={false}
                                headerFilterVisible={true}
                                searchPanelVisible={false}
                                columnChooser={false}
                                enableCustomToolbarContent={false}
                                editingConfig={{
                                    mode: 'batch', allowAdding: true, allowDeleting: true, allowUpdating: true, confirmDelete: true, confirmDeleteMessage: RemoveMessage('Barcode')
                                }}
                                pagerVisible={true}
                                columns={[
                                    {
                                        dataField: 'barcode', caption: t('barcode'), validationRules: [
                                            { type: 'required', message: 'This field is required.' },
                                            {
                                                type: 'stringLength',
                                                max: 30,
                                                message: stringFormat(t('cannot_exceed_characters'), t('barcode'), 30)
                                            }
                                        ]
                                    },
                                    {
                                        dataField: 'barcodeOfBox', caption: t('barcode_of_box'), validationRules: [
                                            {
                                                type: 'stringLength',
                                                max: 30,
                                                message: stringFormat(t('cannot_exceed_characters'), t('barcodeOfBox'), 30)
                                            }
                                        ]
                                    }
                                ]}
                            />


                        </div>
                    </div>

                </div>

            </div>

        </>

    );
}

export default ProductDefinition;
